/** Brand files come from the project's pack (`/v1/brand/<file>`); nothing client-specific is built in. */

const BRAND_FILE = /^[A-Za-z0-9][A-Za-z0-9_-]*(?:\.[A-Za-z0-9_-]+)*$/;

/** URL of a file in the pack's brand folder, or null for anything that is not a plain file name. */
export function brandFileUrl(name: unknown): string | null {
  if (typeof name !== 'string' || !BRAND_FILE.test(name)) return null;
  return `/v1/brand/${encodeURIComponent(name)}`;
}

export interface BrandAssets {
  logoUrl: string | null;
  bannerUrl: string | null;
}

/** Logo and banner URLs from a project's `brand` block (an untyped bag from the config or `/v1/brand`). */
export function brandAssets(brand: unknown): BrandAssets {
  const b = typeof brand === 'object' && brand !== null ? (brand as Record<string, unknown>) : {};
  return { logoUrl: brandFileUrl(b['logo']), bannerUrl: brandFileUrl(b['banner']) };
}

/** A theme's card icon: `brand/theme-<id>.svg` in the pack (a one-colour line drawing, used as a mask). */
export function themeIconUrl(themeId: string): string | null {
  return brandFileUrl(`theme-${themeId}.svg`);
}

/** Up to two initials for the user button: from the name, else the email. */
export function initials(name: string | null | undefined, email: string): string {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
  const out = (first + last).toUpperCase();
  return out || (email.trim()[0] ?? '?').toUpperCase();
}

/** Browser tab title: "<Theme> | <Project>", as production keeps it in step with the active theme. */
export function documentTitle(projectTitle: string, themeTitle?: string | null): string {
  return themeTitle ? `${themeTitle} | ${projectTitle}` : projectTitle;
}

/** A text field from a project's `brand` block (e.g. `shortTitle`, `tagline`), or null when absent or blank. */
export function brandText(brand: unknown, key: 'shortTitle' | 'tagline'): string | null {
  const b = typeof brand === 'object' && brand !== null ? (brand as Record<string, unknown>) : {};
  const value = b[key];
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}
